"use client";

import { useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const exhibitions = [{
  id: "winter-2024",
  season: "WINTER 2024",
  title: "Voices of Change"
}, {
  id: "autumn-2023",
  season: "AUTUMN 2023",
  title: "Truth in Reporting"
}, {
  id: "spring-2023",
  season: "SPRING 2023",
  title: "Community Perspectives"
}, {
  id: "winter-2022",
  season: "WINTER 2022",
  title: "Founding Stories"
}];
function ArchiveGrid({
  activeLabel
}: {
  activeLabel: string;
}) {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-3 gap-4 md:grid-cols-6 md:gap-4">
        {[...Array(18)].map((_, i) => <div key={i} data-index={i} className="aspect-square bg-muted animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-muted-foreground font-mono uppercase tracking-wider">
        Unable to load multimedia content.
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-sm text-muted-foreground font-mono uppercase tracking-wider">
        No multimedia content available.
      </div>;
  }
  const items = posts.slice(0, 18);
  const count = items.length;
  return <>
      <div className="mb-6 flex items-center justify-between border-b border-border pb-4">
        <p className="font-mono text-xs uppercase tracking-[0.08em] text-muted-foreground">
          {"From: " + activeLabel + " \u2014 " + count + " selected stories"}
        </p>
        <p className="hidden md:block font-mono text-xs uppercase tracking-[0.08em] text-muted-foreground">
          {"01 / " + String(count).padStart(2, "0")}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4 md:grid-cols-6 md:gap-4">
        {items.map((post, i) => <figure key={post.id} data-index={i} className="group relative aspect-square overflow-hidden border-b border-border transition-colors duration-300 ease-out hover:border-primary">
            {post.featuredImage ? <img src={post.featuredImage} alt={post.title || "Multimedia story"} loading="lazy" decoding="async" className="h-full w-full object-cover" /> : <div className="h-full w-full bg-muted" />}
            <figcaption className="pointer-events-none absolute inset-0 flex items-end p-3 opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100">
              <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-background bg-foreground/85 px-2 py-1">
                {post.author?.name || "Inclusive Voices"}
              </span>
            </figcaption>
          </figure>)}
      </div>
    </>;
}
export default function Spotlight() {
  const [active, setActive] = useState(exhibitions[0].id);
  const activeExhibition = exhibitions.find(e => e.id === active) || exhibitions[0];
  const activeLabel = activeExhibition.season + " \u00B7 " + activeExhibition.title;
  const wp_query_params = {
    post_type: "post",
    per_page: 18,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="19604" id="multimedia-spotlight" className="dark relative bg-background py-32 md:py-44 border-t border-border">
      <div className="mx-auto w-full max-w-[1440px] px-6 md:px-12">
        {}
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-6">
          {}
          <div className="md:col-span-6">
            <p className="font-mono text-xs uppercase tracking-[0.08em] text-muted-foreground mb-8">
              {"\u00A7 Multimedia \u00B7 2022 \u2014 2024"}
            </p>
            <h2 className="font-default font-bold text-foreground leading-[1.0] tracking-[-0.028em] text-[clamp(2.25rem,4.4vw,4.25rem)]">
              {"Featured "}
              <em className="italic font-medium">multimedia</em>
              {"."}
            </h2>
            <p className="mt-8 max-w-[42ch] font-default text-foreground/90 leading-[1.5] tracking-[-0.005em] text-[clamp(1.125rem,1.4vw,1.375rem)]">
              A curated collection of our most impactful stories. From grassroots reporting to deep-dive documentaries, we amplify the voices that shape our community and drive the conversation forward.
            </p>
          </div>

          {}
          <div className="md:col-span-6 md:pl-6">
            <p className="font-mono text-xs uppercase tracking-[0.08em] text-muted-foreground mb-6">
              {"Select category"}
            </p>

            {}
            <Tabs value={active} onValueChange={setActive} className="hidden md:block">
              <TabsList className="flex h-auto w-full flex-col items-stretch gap-0 bg-transparent p-0">
                {exhibitions.map((ex, i) => <TabsTrigger key={ex.id} value={ex.id} data-index={i} className="group relative w-full flex-none justify-between border-0 border-b border-border bg-transparent px-0 py-6 text-left font-default text-foreground transition-none data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                    <span className="flex w-full items-baseline justify-between gap-6">
                      <span className="flex items-baseline gap-4">
                        <span className="font-mono text-xs uppercase tracking-[0.08em] text-muted-foreground">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="font-mono text-xs uppercase tracking-[0.08em] text-muted-foreground">
                          {ex.season}
                        </span>
                        <span className="font-default text-base md:text-lg font-semibold tracking-[-0.018em] text-foreground">
                          {ex.title}
                        </span>
                      </span>
                      <span aria-hidden="true" className={"block h-px transition-all duration-220 ease-out " + (active === ex.id ? "w-12 bg-primary" : "w-6 bg-border group-hover:w-10 group-hover:bg-foreground")} />
                    </span>
                  </TabsTrigger>)}
              </TabsList>
            </Tabs>

            {}
            <div className="md:hidden -mx-6 overflow-x-auto">
              <div className="flex min-w-max items-stretch border-y border-border">
                {exhibitions.map((ex, i) => {
                const isActive = ex.id === active;
                return <button key={ex.id} data-index={i} type="button" onClick={() => setActive(ex.id)} className={"flex flex-col items-start gap-1 border-r border-border px-5 py-4 text-left transition-colors " + (isActive ? "bg-muted" : "bg-transparent")}>
                      <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                        {ex.season}
                      </span>
                      <span className="font-default text-sm font-semibold tracking-[-0.018em] text-foreground">
                        {ex.title}
                      </span>
                      <span aria-hidden="true" className={"mt-2 block h-px transition-all duration-220 ease-out " + (isActive ? "w-8 bg-primary" : "w-4 bg-border")} />
                    </button>;
              })}
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="mt-20 md:mt-24">
          <PostsProvider wp_query={{
          post_type: "post",
          per_page: 18,
          order: "desc",
          orderby: "date"
        }}>
            <ArchiveGrid activeLabel={activeLabel} />
          </PostsProvider>
        </div>
      </div>
    </section>;
}